import React from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart as ChartComponent } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { FinancialChartProps } from './types/chartTypes';
import { useFinancialChart } from './hooks/useFinancialChart';
import { CHART_DIMENSIONS } from './constants/chartConstants';

// Chart.jsの全コンポーネント登録
ChartJS.register(...registerables);

export const FinancialChart: React.FC<FinancialChartProps> = ({
  data,
  period,
  accountType = 'both',
  showBalanceLine = true,
  showCumulativeBalance = false,
  onChartClick,
  height = CHART_DIMENSIONS.DEFAULT_HEIGHT,
  className,
  currentFiscalYear,
}) => {
  const {
    chartData,
    chartOptions,
    handleChartClick,
    isLoading,
    error,
  } = useFinancialChart({
    data,
    period,
    accountType,
    showBalanceLine,
    showCumulativeBalance,
    onChartClick,
    currentFiscalYear,
  });

  if (error) {
    return (
      <Alert severity="error" sx={{ margin: 2 }}>
        グラフの読み込みでエラーが発生しました: {error}
      </Alert>
    );
  }

  if (isLoading || !data || data.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={height}
        className={className}
      >
        <Box textAlign="center">
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            データを読み込んでいます...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (chartData.labels.length === 0) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={height}
        className={className}
      >
        <Typography variant="body1" color="text.secondary">
          表示するデータがありません
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      height={height} 
      width="100%" 
      className={className}
      sx={{ 
        position: 'relative',
        '& canvas': {
          cursor: 'default',
        }
      }}
    >
      <ChartComponent 
        type="bar"
        data={chartData as ChartData<'bar', number[], string>}
        options={chartOptions as ChartOptions<'bar'>}
      />
    </Box>
  );
};